import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

import { useMediaQuery } from '@material-ui/core';

enum EDarkModeSettings {
	SYSTEM = 'system',
	ON = 'on',
	OFF = 'off',
}

enum EThemePaletteTypes {
	LIGHT = 'light',
	DARK = 'dark',
}

const DARK_MODE_STORAGE_KEY = 'asaphly_dark_mode';

type ContextData = {
	palette: EThemePaletteTypes;
	darkModeSetting: EDarkModeSettings;
	setDarkModeSetting: (mode: EDarkModeSettings) => void;
	isDarkModeOn: boolean;
	handleModeChange: (args: {
		temp?: boolean;
		mode: EDarkModeSettings;
	}) => void;
	getDefaultDarkMode: () => EDarkModeSettings;
};

const defaultContextData: ContextData = {
	palette: EThemePaletteTypes.LIGHT,
	darkModeSetting: EDarkModeSettings.SYSTEM,
	setDarkModeSetting: (mode: EDarkModeSettings): void => {},
	isDarkModeOn: false,
	handleModeChange: () => {},
	getDefaultDarkMode: () => EDarkModeSettings.SYSTEM,
};

const DarkModeContext = createContext(defaultContextData);

const useDarkMode = () => useContext(DarkModeContext);

const DarkModeProvider: React.FC = ({ children }) => {
	const {
		palette,
		darkModeSetting,
		setDarkModeSetting,
		isDarkModeOn,
		handleModeChange,
		getDefaultDarkMode,
	} = useEffectDarkMode();

	return (
		<DarkModeContext.Provider
			value={{
				palette,
				darkModeSetting,
				setDarkModeSetting,
				isDarkModeOn,
				handleModeChange,
				getDefaultDarkMode,
			}}
		>
			{children}
		</DarkModeContext.Provider>
	);
};

const useEffectDarkMode = (): typeof defaultContextData => {
	const [darkModeSetting, _setDarkModeSetting] = useState<EDarkModeSettings>(
		getDefaultDarkMode(),
	);

	const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

	// Uncomment these lines to turn dark mode feature
	// const palette =
	// 	darkModeSetting === EDarkModeSettings.ON ||
	// 	(darkModeSetting === EDarkModeSettings.SYSTEM && prefersDarkMode)
	// 		? EThemePaletteTypes.DARK
	// 		: EThemePaletteTypes.LIGHT;
	// const isDarkModeOn = palette === EThemePaletteTypes.DARK;

	const palette = EThemePaletteTypes.LIGHT;
	const isDarkModeOn = false;

	const handleModeChange = useCallback<ContextData['handleModeChange']>(
		({ temp = true, mode }) => {
			_setDarkModeSetting(mode);
			document.body.classList.add(getClassForMode(mode));
			if (mode !== EDarkModeSettings.SYSTEM) {
				document.body.classList.remove(
					getClassForMode(EDarkModeSettings.SYSTEM),
				);
			}
			if (mode !== EDarkModeSettings.ON) {
				document.body.classList.remove(
					getClassForMode(EDarkModeSettings.ON),
				);
			}
			if (mode !== EDarkModeSettings.OFF) {
				document.body.classList.remove(
					getClassForMode(EDarkModeSettings.OFF),
				);
			}

			if (!temp) {
				window.localStorage.setItem(DARK_MODE_STORAGE_KEY, mode);
			}
		},
		[],
	);

	const setDarkModeSetting = useCallback(
		(mode: EDarkModeSettings) => {
			handleModeChange({ temp: false, mode });
		},
		[handleModeChange],
	);

	useEffect(() => {
		handleModeChange({ temp: true, mode: darkModeSetting });
	}, [darkModeSetting, handleModeChange]);

	useEffect(() => {
		function handleStorageChange(event: StorageEvent) {
			if (event.key === DARK_MODE_STORAGE_KEY) {
				const mode = parseModeFromStorage(event.newValue);
				setDarkModeSetting(mode);
			}
		}

		window.addEventListener('storage', handleStorageChange);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
		};
	}, [setDarkModeSetting]);

	function getDefaultDarkMode(): EDarkModeSettings {
		if (typeof window !== 'undefined') {
			const mode = window.localStorage.getItem(DARK_MODE_STORAGE_KEY);
			// return parseModeFromStorage(mode);
			return EDarkModeSettings.OFF;
		}

		return EDarkModeSettings.SYSTEM;
	}

	return {
		palette,
		darkModeSetting,
		setDarkModeSetting,
		isDarkModeOn,
		handleModeChange,
		getDefaultDarkMode,
	};
};

function parseModeFromStorage(mode: string | null): EDarkModeSettings {
	if (mode && (Object as any).values(EDarkModeSettings).includes(mode)) {
		return mode as EDarkModeSettings;
	}
	return EDarkModeSettings.SYSTEM;
}

function getClassForMode(mode: EDarkModeSettings) {
	switch (mode) {
		case EDarkModeSettings.SYSTEM:
			return 'system';
		case EDarkModeSettings.ON:
			return 'dark';
		default:
			return 'light';
	}
}

export {
	EDarkModeSettings,
	EThemePaletteTypes,
	useDarkMode,
	DarkModeProvider,
	getClassForMode,
};
